import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Button,
  Checkbox,
  DataTable,
  InputText,
  NumberInput,
  Select,
  type ColumnDef,
  type SelectOption,
  SectionCard,
} from "@/deps/ui-library";
import { api } from "@/api";
import {
  useBankAccounts,
  useOpenBills,
  usePartyDetails,
  useRecordSupplierPayment,
  useWithholdingCodes,
} from "@/api/queries";
import type { BillSummary, Party } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { PartyCombobox } from "@/components/PartyCombobox";
import { useStatementLineStart } from "@/features/banking/reconciliation/fromStatementLine";
import { usePartyFromAddress } from "@/features/parties/usePartyFromAddress";
import { SegmentControl } from "@/components/SegmentControl";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { byEarliestDue, earliestDueFirst, withholdingBase } from "@/lib/settlement";
import { useTaxWords } from "@/app/session";

const METHODS = ["Bank transfer", "Cheque", "Cash", "eSewa", "ConnectIPS"];

/** Grouping off, because the screen parses the string: "2,500" would parse as nothing. */
const MONEY = { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 };

const paisa = (value: string) => Math.round(Number(value || "0") * 100);
const rupees = (value: number) => (value / 100).toFixed(2);

type Mode = "bill" | "new";
type OpenRow = BillSummary & { applying: string };

function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

/** Built once: a rebuilt cell is a new input that has lost the caret mid-number. */
function openBillColumns(
  setApplying: (billId: string, value: string) => void,
): Array<ColumnDef<OpenRow>> {
  return [
    {
      id: "number",
      header: "Bill",
      cell: ({ row: { original: bill } }) => <span className="num">{bill.number}</span>,
    },
    {
      id: "due",
      header: "Due",
      cell: ({ row: { original: bill } }) => (
        <span className="text-muted-foreground text-xs">{formatDate(bill.due)}</span>
      ),
    },
    {
      id: "balance",
      header: "Balance",
      meta: { textAlign: "right" },
      cell: ({ row: { original: bill } }) => (
        <span className="num">{formatAmount(bill.balance)}</span>
      ),
    },
    {
      id: "apply",
      header: "Apply",
      size: 140,
      meta: { textAlign: "right" },
      cell: ({ row: { original: bill } }) => (
        <NumberInput
          aria-label={`Amount to apply to ${bill.number}`}
          invalid={paisa(bill.applying) > paisa(bill.balance)}
          value={bill.applying}
          onValueChange={(details) => setApplying(bill.id, details.value)}
          formatOptions={MONEY}
          config={{ hideTriggers: true }}
          className="ml-auto w-28"
          inputProps={{ className: "num pr-3 text-right" }}
        />
      ),
    },
    {
      id: "full",
      header: "",
      size: 60,
      cell: ({ row: { original: bill } }) => (
        <Button
          colorScheme="default"
          variant="ghost"
          size="xs"
          aria-label={`Apply the full balance to ${bill.number}`}
          onClick={() => setApplying(bill.id, bill.balance)}
        >
          Full
        </Button>
      ),
    },
  ];
}

/**
 * Record a payment to a supplier.
 *
 * It starts from a bill, which presets the supplier and the allocation, or from any supplier, where
 * the amount settles the earliest due bill first (ADR-0035) and what no bill takes is held as an
 * advance. The person can apply it elsewhere. The gross is what settles bills; only the net leaves
 * the bank, and the difference is owed to the revenue authority instead.
 */
export function RecordPaymentPage() {
  const say = useTaxWords();
  const navigate = useNavigate();
  const { bill: billFromLink } = useSearch({ from: "/purchases/payments/new" });
  const fromLine = useStatementLineStart();
  const [mode, setMode] = useState<Mode>(billFromLink ? "bill" : "new");
  const [billId, setBillId] = useState(billFromLink ?? "");
  const [supplier, setSupplier] = useState<Party | null>(null);
  usePartyFromAddress("supplier", setSupplier);
  const [date, setDate] = useState(fromLine.start?.date ?? today());
  const [method, setMethod] = useState("Bank transfer");
  const [reference, setReference] = useState("");
  const [gross, setGross] = useState(fromLine.start?.amount ?? "");
  // Null until someone changes an application: until then, the gross settles earliest due first.
  const [typed, setTyped] = useState<Record<string, string> | null>(null);
  const [withholds, setWithholds] = useState(false);
  const [code, setCode] = useState("");

  const { data: bankAccounts } = useBankAccounts();
  const [bankAccountId, setBankAccountId] = useState(fromLine.start?.account ?? "");
  const account = bankAccounts?.find((a) => a.id === bankAccountId) ?? bankAccounts?.[0];

  const { data: codes } = useWithholdingCodes();
  const record = useRecordSupplierPayment();

  const { data: allOpen } = useOpenBills(undefined, mode === "bill");
  const { data: supplierOpen } = useOpenBills(supplier?.id, mode === "new" && supplier !== null);
  const fromBill = allOpen?.find((b) => b.id === billId);

  const open = useMemo(() => {
    if (mode === "bill") return fromBill ? [fromBill] : [];
    return supplier === null ? [] : (supplierOpen ?? []);
  }, [fromBill, mode, supplier, supplierOpen]);

  // Choosing a bill presets what it owes. It is a starting point the person can then change.
  const presetFor = fromBill?.id;
  const presetBalance = fromBill?.balance;
  useEffect(() => {
    if (presetFor === undefined || presetBalance === undefined) return;
    setGross(presetBalance);
    setTyped(null);
  }, [presetFor, presetBalance]);

  const supplierId = mode === "bill" ? fromBill?.supplierId : supplier?.id;

  // The supplier's usual category starts it off, and it can be changed or unticked. Moving on to a
  // supplier with none clears only what was preselected, not what someone chose themselves.
  const { data: supplierDetails } = usePartyDetails(supplierId ?? "", supplierId !== undefined);
  const usualCode = supplierId === undefined ? undefined : supplierDetails?.withholdingCode;
  const preselected = useRef(false);
  useEffect(() => {
    if (usualCode === undefined) return;
    if (usualCode !== "") {
      setWithholds(true);
      setCode(usualCode);
      preselected.current = true;
    } else if (preselected.current) {
      setWithholds(false);
      setCode("");
      preselected.current = false;
    }
  }, [supplierId, usualCode]);

  const chooseMode = (next: Mode) => {
    setMode(next);
    setTyped(null);
    setGross("");
    setBillId("");
    setSupplier(null);
  };

  // The rate is the category's, and the amount follows from it: neither is typed here (ADR-0031).
  const category = codes?.find((c) => c.code === code);
  const rate = category?.ratePct ?? "";

  const grossTotal = paisa(gross);
  const applied = typed ?? earliestDueFirst(open, grossTotal);
  const visibleApplied = open.reduce((sum, bill) => sum + paisa(applied[bill.id] ?? ""), 0);
  const advance = grossTotal - visibleApplied;
  // TDS is charged before VAT, on what goes on a bill and never on an advance.
  const taxBase = open.reduce(
    (sum, bill) => sum + withholdingBase(bill, paisa(applied[bill.id] ?? "")),
    0,
  );
  const withheldTotal = withholds ? Math.round((taxBase * Number(rate || "0")) / 100) : 0;
  const net = grossTotal - withheldTotal;
  const overApplied = open.filter((bill) => paisa(applied[bill.id] ?? "") > paisa(bill.balance));
  const overGross = visibleApplied > grossTotal;
  // What left the bank is the net, and a match has to agree exactly. With tax withheld, the gross is
  // larger than the statement line.
  const offTheLine = fromLine.start !== null && net !== paisa(fromLine.start.amount);

  const canSave =
    supplierId !== undefined &&
    account !== undefined &&
    grossTotal > 0 &&
    overApplied.length === 0 &&
    !overGross &&
    (!withholds || (category !== undefined && Number(rate) > 0)) &&
    !offTheLine &&
    !record.isPending;

  const save = () => {
    record.mutate(
      {
        supplierId: supplierId!,
        date,
        bankAccountId: account!.id,
        method,
        reference: reference.trim(),
        gross: rupees(grossTotal),
        ...(withholds ? { withholding: { code } } : {}),
        allocations: open
          .filter((bill) => paisa(applied[bill.id] ?? "") > 0)
          .map((bill) => ({ billId: bill.id, amount: rupees(paisa(applied[bill.id] ?? "")) })),
      },
      {
        onSuccess: (payment) => {
          // A draft has no ledger entry to match yet, so the line waits until it's posted.
          if (fromLine.start && payment.status === "draft") {
            fromLine.back(
              "This payment needs approval, so it's been saved as a draft. Match the line once it's posted.",
            );
          } else if (fromLine.start) void fromLine.finish(payment.journalId);
          else void navigate({ to: "/purchases/payments/$id", params: { id: payment.id } });
        },
      },
    );
  };

  // The columns are built once, so a cell reads what is applied now through this. RDS also reports
  // the value it was handed, and only a figure that differs is someone changing it.
  const appliedNow = useRef(applied);
  appliedNow.current = applied;
  const columns = useMemo(
    () =>
      openBillColumns((billId, value) => {
        if (paisa(value) === paisa(appliedNow.current[billId] ?? "")) return;
        setTyped({ ...appliedNow.current, [billId]: value });
      }),
    [],
  );
  const rows: OpenRow[] = byEarliestDue(open).map((bill) => ({
    ...bill,
    applying: applied[bill.id] ?? "",
  }));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Record payment"
        actions={
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() =>
                fromLine.start ? fromLine.back() : void navigate({ to: "/purchases/payments" })
              }
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={!canSave}
              onClick={save}
            >
              {record.isPending ? "Saving" : "Save payment"}
            </Button>
          </span>
        }
      />

      <SegmentControl<Mode>
        label="Start from"
        segments={[
          { value: "bill", label: "From a bill" },
          { value: "new", label: "New payment" },
        ]}
        value={mode}
        onChange={chooseMode}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(360px,1fr)_minmax(420px,1.2fr)] lg:items-start">
        <SectionCard title="Payment">
          <div className="flex flex-col gap-3">
            {mode === "bill" ? (
              <>
                <Select
                  label="Bill"
                  required
                  options={(allOpen ?? []).map((bill) => ({
                    value: bill.id,
                    label: `${bill.number} · ${bill.supplierName} · ${formatAmount(bill.balance)}`,
                  }))}
                  value={billId}
                  onChange={(details) => setBillId(chosen(details))}
                  showClearButton={false}
                />
                {fromBill ? (
                  <p className="text-fg-muted text-xs">
                    Supplier <span className="text-fg">{fromBill.supplierName}</span>
                  </p>
                ) : null}
              </>
            ) : (
              <PartyCombobox
                label="Supplier"
                required
                value={supplier}
                onChange={(party) => {
                  setSupplier(party);
                  setTyped(null);
                }}
                search={(q) => api.parties.search("supplier", q)}
              />
            )}

            <InputText
              label="Date"
              required
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />

            <Select
              label="Method"
              required
              options={METHODS.map((option) => ({ value: option, label: option }))}
              value={method}
              onChange={(details) => {
                const picked = chosen(details);
                if (picked) setMethod(picked);
              }}
              showClearButton={false}
            />

            <InputText
              label="Cheque or reference number"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />

            <Select
              label="Paid from"
              required
              options={(bankAccounts ?? []).map((option) => ({
                value: option.id,
                label: option.name,
              }))}
              value={account?.id ?? ""}
              onChange={(details) => setBankAccountId(chosen(details))}
              showClearButton={false}
            />

            <NumberInput
              label="Gross amount · before withholding"
              required
              value={gross}
              onValueChange={(details) => setGross(details.value)}
              formatOptions={MONEY}
              config={{ hideTriggers: true }}
            />

            <Checkbox
              label="Deduct tax at source"
              checked={withholds}
              onCheckedChange={(details) => setWithholds(details.checked === true)}
            />

            {withholds ? (
              <div className="flex flex-col gap-1">
                <Select
                  label="Category"
                  required
                  options={(codes ?? []).map((option) => ({
                    value: option.code,
                    label: option.ratePct ? `${option.name} · ${option.ratePct}%` : option.name,
                  }))}
                  value={code}
                  onChange={(details) => setCode(chosen(details))}
                  showClearButton={false}
                />
                {category ? (
                  <p className="text-fg-muted text-xs">
                    {rate}% of <span className="num">{formatAmount(rupees(taxBase))}</span>, the{" "}
                    {say("amount before VAT.")}
                    {advance > 0 ? " Nothing is withheld on the advance." : ""}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </SectionCard>

        <SectionCard
          title="Apply to bills"
          actions={
            open.length > 0 ? (
              <span className="flex gap-1">
                <Button
                  colorScheme="default"
                  variant="ghost"
                  size="sm"
                  disabled={typed === null}
                  onClick={() => setTyped(null)}
                >
                  Apply earliest due first
                </Button>
                <Button
                  colorScheme="default"
                  variant="ghost"
                  size="sm"
                  onClick={() => setTyped({})}
                >
                  Clear
                </Button>
              </span>
            ) : null
          }
          flush
        >
          {supplierId === undefined ? (
            <p className="text-fg-muted p-4 text-sm">
              {mode === "bill"
                ? "Choose a bill to pay."
                : "Choose a supplier to see what you owe them."}
            </p>
          ) : open.length === 0 ? (
            <p className="text-fg-muted p-4 text-sm">
              Nothing is outstanding. Anything paid will be held as a supplier advance.
            </p>
          ) : (
            <DataTable
              aria-label="Open bills"
              size="sm"
              enableSorting={false}
              columns={columns}
              data={rows}
              getRowId={(bill) => bill.id}
              pagination={{ mode: "manual", show: false }}
            />
          )}

          <div className="border-line flex flex-col gap-1 border-t px-3 py-2 text-xs">
            <div className="flex items-baseline justify-between">
              <span className="text-fg-muted">Gross · settles the bill</span>
              <span className="num text-fg">{formatAmount(rupees(grossTotal))}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-fg-muted">
                Less: tax withheld{withholds && rate ? ` at ${rate}%` : ""}
              </span>
              <span className="num text-fg">{formatAmount(rupees(withheldTotal))}</span>
            </div>
            <div className="flex items-baseline justify-between font-semibold">
              <span className="text-fg">Net paid from {account?.name ?? "the bank"}</span>
              <span className="num text-fg">{formatAmount(rupees(net))}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-fg-muted">Applied to bills</span>
              <span className="num text-fg">{formatAmount(rupees(visibleApplied))}</span>
            </div>
            {advance > 0 ? (
              <div className="flex items-baseline justify-between">
                <span className="text-warn-text">Unapplied · held as supplier advance</span>
                <span className="num text-warn-text">{formatAmount(rupees(advance))}</span>
              </div>
            ) : null}

            {overApplied.length > 0 ? (
              <p role="alert" className="text-bad-text">
                {overApplied.map((bill) => bill.number).join(", ")} would be over-settled. Apply no
                more than the balance.
              </p>
            ) : null}
            {offTheLine ? (
              <p role="alert" className="text-bad-text">
                The net paid has to be {formatAmount(fromLine.start!.amount)}, the amount on the
                statement line, for the two to match.
              </p>
            ) : null}
            {overGross ? (
              <p role="alert" className="text-bad-text">
                Applied exceeds the gross amount by {formatAmount(rupees(-advance))}. Reduce an
                allocation before saving.
              </p>
            ) : null}
            {record.isError ? (
              <p role="alert" className="text-bad-text">
                {record.error instanceof Error
                  ? record.error.message
                  : "That payment could not be saved."}
              </p>
            ) : null}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
